import React, { useState } from "react";
import { motion } from "@/lib/framer-motion-shim";
import { Scissors, Instagram, Facebook, Phone, MapPin, Clock, Star, Calendar } from "lucide-react";
import { useLocation } from "react-router-dom";
import BookingModal from "@/components/demos/BookingModal";
import ContactForm from "@/components/demos/ContactForm";
import DemoTopBar from "@/components/demos/DemoTopBar";
import Seo from "@/components/Seo";

const images = [
  "https://images.unsplash.com/photo-1759134248487-e8baaf31e33e?w=800&q=80",
  "https://images.unsplash.com/photo-1761931403663-050fbd8fb4c8?w=800&q=80",
  "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?w=800&q=80",
  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800&q=80",
];

const services = [
  { name: "Corte Feminino", price: "35€", duration: "45min" },
  { name: "Coloração Completa", price: "80€", duration: "2h" },
  { name: "Madeixas", price: "95€", duration: "2.5h" },
  { name: "Manicure & Pedicure", price: "45€", duration: "1h" },
  { name: "Tratamento Facial", price: "60€", duration: "1h" },
  { name: "Brushing", price: "25€", duration: "30min" },
];

const testimonials = [
  { name: "Maria S.", text: "Ambiente fantástico e profissionais muito atenciosos. Adorei o resultado!", rating: 5 },
  { name: "Ana P.", text: "O melhor salão da cidade! Sempre saio de lá a sentir-me renovada.", rating: 5 },
  { name: "Sofia L.", text: "Qualidade excelente e preços justos. Recomendo a 100%!", rating: 5 },
];

export default function SalonDemo() {
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("agendar") === "1") {
      setBookingOpen(true);
    }
  }, [location.search]);

  return (
    <>
      <Seo
        title="Demo Salão de Beleza | Web & Studio"
        description="Template demo para salão de beleza com tabela de preços, galeria, testemunhos e formulário de contacto."
        path="/demo/salon"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-rose-50 text-stone-800 theme-salon"
      >
        <DemoTopBar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Scissors className="w-6 h-6 text-rose-400" />
                <span className="text-rose-400 uppercase tracking-[0.2em] text-sm">Salão de Beleza</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-semibold leading-tight mb-6">Belle Beauté</h1>
              <p className="text-xl text-stone-600 leading-relaxed mb-8 max-w-md">
                Um refúgio de beleza e bem-estar no coração da cidade. 
                Onde cada detalhe é pensado para si.
              </p>
              <button
                onClick={() => setBookingOpen(true)}
                className="group flex items-center gap-3 px-8 py-4 bg-rose-400 text-white rounded-full hover:bg-rose-500 transition-colors duration-200"
                data-testid="hero-booking-btn"
              >
                <Calendar className="w-5 h-5" />
                Marcar Agora
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <img
                src={images[2]}
                alt="Salon"
                className="w-full aspect-[4/5] object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-rose-400 text-rose-400" />
                  ))}
                </div>
                <p className="text-sm text-stone-500">+500 clientes satisfeitas</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-rose-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-rose-100/50 rounded-full blur-2xl" />
      </section>

      {/* Services */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-rose-400 uppercase tracking-[0.2em] text-sm">Os Nossos Serviços</span>
            <h2 className="text-4xl md:text-5xl font-semibold mt-4">Tabela de Preços</h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group flex items-center justify-between py-6 border-b border-rose-100 hover:bg-rose-50/50 px-4 -mx-4 transition-colors duration-200 cursor-pointer"
                onClick={() => setBookingOpen(true)}
                data-testid={`service-item-${index}`}
              >
                <div className="flex items-center gap-4">
                  <h3 className="text-xl md:text-2xl font-medium">{service.name}</h3>
                  <span className="text-sm text-stone-400 hidden md:block">{service.duration}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-rose-400 text-lg">..................</span>
                  <span className="text-2xl font-medium">{service.price}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => setBookingOpen(true)}
              className="px-8 py-4 bg-rose-400 text-white rounded-full hover:bg-rose-500 transition-colors duration-200"
              data-testid="services-booking-btn"
            >
              Marcar Serviço
            </button>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-rose-400 uppercase tracking-[0.2em] text-sm">Galeria</span>
            <h2 className="text-4xl md:text-5xl font-semibold mt-4">O Nosso Espaço</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`${index === 0 ? 'col-span-2 row-span-2' : ''} relative group overflow-hidden rounded-2xl`}
              >
                <img
                  src={img}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover aspect-square transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-rose-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-rose-400 uppercase tracking-[0.2em] text-sm">Testemunhos</span>
            <h2 className="text-4xl md:text-5xl font-semibold mt-4">O Que Dizem de Nós</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-rose-50 rounded-2xl"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-rose-400 text-rose-400" />
                  ))}
                </div>
                <p className="text-lg mb-4 italic">"{testimonial.text}"</p>
                <p className="font-medium">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-rose-400 uppercase tracking-[0.2em] text-sm">Contacto</span>
              <h2 className="text-4xl md:text-5xl font-semibold mt-4 mb-8">Visite-nos</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-rose-400" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Morada</p>
                    <p className="text-lg">Av. da Liberdade, 456 - Porto</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-rose-400" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Telefone</p>
                    <p className="text-lg">+351 923 456 789</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-rose-400" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Horário</p>
                    <p className="text-lg">Ter-Sáb: 9h-19h</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center hover:bg-rose-200 transition-colors duration-200">
                  <Instagram className="w-5 h-5 text-rose-400" />
                </a>
                <a href="#" className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center hover:bg-rose-200 transition-colors duration-200">
                  <Facebook className="w-5 h-5 text-rose-400" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-semibold mb-6">Envie-nos uma mensagem</h3>
              <ContactForm demoType="salon" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-rose-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-stone-500">© 2024 Belle Beauté</p>
            <p className="text-stone-400 text-sm">Demo Template - Salão de Beleza</p>
          </div>
        </div>
      </footer>

        <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} demoType="salon" />
      </motion.div>
    </>
  );
}




